import type { PortalAudienceRule } from '../../types/portalLibrary.js';
import type { InspirationUsage, PortalInspirationEntry } from '../../types/portalInspiration.js';
import { INSPIRATION_USAGES } from '../../types/portalInspiration.js';

// PORTAL INSPIRATION — entry rules. PURE; shared by the staff form and the API so the
// two cannot disagree about what "publishable" means.

export const INSPIRATION_TITLE_MAX = 120;
export const INSPIRATION_TREND_LINE_MAX = 240;
export const INSPIRATION_SUGGESTED_LINES_MAX = 3;
export const INSPIRATION_SUGGESTED_LINE_MAX = 100;
export const INSPIRATION_DESCRIPTION_MAX = 800;
export const INSPIRATION_HOWTO_MAX = 1500;
export const INSPIRATION_MAX_TAGS = 10;

export function isInspirationUsage(v: unknown): v is InspirationUsage {
  return typeof v === 'string' && (INSPIRATION_USAGES as readonly string[]).includes(v);
}

type Publishable = Pick<PortalInspirationEntry, 'title' | 'description' | 'howTo' | 'usage' | 'audience'>;

/**
 * What stops a draft being published. Empty = publishable.
 * ⚠️ Title, description, how-to and usage. The AUDIENCE rule is the HTML uploader's exactly: it must reach
 * at least one account (inspirationReachRefusal, checked by the service) — NO industry is required, so a
 * marque-only entry stays marque-only.
 */
export function publishProblems(entry: Publishable): string[] {
  const out: string[] = [];
  if (entry.title.trim() === '') out.push('Add a title.');
  if (entry.description.trim() === '') out.push('Add a description.');
  if (entry.howTo.trim() === '') out.push('Add a how-to.');
  if (entry.usage === null) out.push('Choose advertising, organic or both.');
  return out;
}

export type BoldSegment = { text: string; bold: boolean };

/**
 * Split `**payoff**` markup into segments for SAFE rendering (React text nodes only —
 * never innerHTML). An unmatched `**` stays literal rather than swallowing the rest.
 */
export function parseInspirationBold(text: string): BoldSegment[] {
  const out: BoldSegment[] = [];
  let rest = text;
  while (rest.length > 0) {
    const open = rest.indexOf('**');
    if (open === -1) break;
    const close = rest.indexOf('**', open + 2);
    if (close === -1) break;
    if (open > 0) out.push({ text: rest.slice(0, open), bold: false });
    const inner = rest.slice(open + 2, close);
    if (inner.length > 0) out.push({ text: inner, bold: true });
    rest = rest.slice(close + 2);
  }
  if (rest.length > 0) out.push({ text: rest, bold: false });
  return out;
}

/** The how-to as steps: one per non-empty line, a leading "1." / "-" / bullet removed. */
export function howToSteps(howTo: string): string[] {
  return howTo
    .split(/\r?\n/)
    .map((l) => l.replace(/^\s*(?:\d+[.)]|[-•*])\s*/, '').trim())
    .filter((l) => l.length > 0);
}

export function fieldLengthProblems(e: {
  title: string;
  trendLine: string | null;
  description: string;
  howTo: string;
  suggestedLines?: readonly string[];
}): string[] {
  const out: string[] = [];
  if ((e.suggestedLines ?? []).length > INSPIRATION_SUGGESTED_LINES_MAX)
    out.push(`Use at most ${INSPIRATION_SUGGESTED_LINES_MAX} suggested lines.`);
  if ((e.suggestedLines ?? []).some((l) => l.length > INSPIRATION_SUGGESTED_LINE_MAX))
    out.push(`A suggested line is over ${INSPIRATION_SUGGESTED_LINE_MAX} characters.`);
  if (e.title.length > INSPIRATION_TITLE_MAX) out.push(`Title is over ${INSPIRATION_TITLE_MAX} characters.`);
  if ((e.trendLine ?? '').length > INSPIRATION_TREND_LINE_MAX) out.push(`Trend line is over ${INSPIRATION_TREND_LINE_MAX} characters.`);
  if (e.description.length > INSPIRATION_DESCRIPTION_MAX)
    out.push(`Description is over ${INSPIRATION_DESCRIPTION_MAX} characters.`);
  if (e.howTo.length > INSPIRATION_HOWTO_MAX) out.push(`How-to is over ${INSPIRATION_HOWTO_MAX} characters.`);
  return out;
}
